import React from "react";
import { motion } from "framer-motion";
import python from '../images/python.png';
import numpy from '../images/python.png';
import ten from '../images/TensorFlow.png';
import ScikitLearn from '../images/Scikit-Learn.png';
import flask from '../images/Flask.png';
import keras from '../images/Keras.png';
import pandas from '../images/pandas.png';
import skillora from '../images/skillora.jpg';
import street from '../images/streetsence.jpg';
import Navbar from "../components/Navbar";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" }
};

export default function ModelTraining() {
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-purple-500/10 overflow-x-hidden font-inter">
      <Navbar />

      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 right-0 w-[1000px] h-[1000px] bg-purple-500/5 blur-[150px] rounded-full -mr-96 -mt-96 animate-blob" />
      <div className="fixed bottom-0 left-0 w-[800px] h-[800px] bg-indigo-500/5 blur-[150px] rounded-full -ml-48 -mb-48 animate-blob delay-4000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      {/* Hero Section */}
      <section className="relative pt-64 pb-32 px-6 flex flex-col items-center text-center overflow-hidden">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-16"
        >
          <div className="absolute -inset-16 bg-purple-500/10 blur-[140px] rounded-full" />
          <h1 className="text-5xl md:text-8xl lg:text-[11.5rem] font-black text-white mb-12 tracking-tighter relative z-10 uppercase leading-[0.8] font-inter px-4">
            Neural <br /><span className="text-gradient from-purple-600 via-indigo-600 to-blue-600">Architect.</span>
          </h1>
        </motion.div>

        <p className="text-lg md:text-2xl lg:text-3xl text-slate-400 max-w-4xl mb-20 leading-relaxed relative z-10 font-medium lowercase tracking-tight px-6">
          engineering the next generation of artificial intelligence. from predictive analytics to
          deep neural networks, we train models that think, learn, and evolve at the speed of logic.
        </p>

        <div className="flex gap-10 relative z-10">
          <a
            href="https://wa.me/94723954658"
            target="_blank"
            rel="noopener noreferrer"
            className="px-16 py-7 bg-slate-900 text-white font-black rounded-[2.5rem] shadow-2xl hover:bg-slate-800 transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-[11px] flex items-center gap-3"
          >
            INITIATE AI SPRINT
            <span className="text-2xl transition-transform group-hover:translate-x-1">→</span>
          </a>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-40 px-6 relative z-10 bg-[#020617] border-y border-white/5 backdrop-blur-sm">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { icon: "📊", title: "Data Synthesis", desc: "Advanced feature engineering and multidimensional data normalization logic." },
              { icon: "🤖", title: "Neural Synthesis", desc: "Training bespoke CNN, RNN, and Transformer architectures for complex decision logic." },
              { icon: "🚀", title: "Edge Deployment", desc: "Seamless model integration via high-concurrency enterprise-grade APIs." },
              { icon: "📈", title: "Hyper-Optimization", desc: "Automated hyperparameter tuning for peak algorithmic precision and throughput." },
              { icon: "🔐", title: "Secure Inference", desc: "Encrypted model endpoints for mission-critical enterprise level data protection." },
              { icon: "🎓", title: "Intellectual Transfer", desc: "Exhaustive technical blueprints and architectural knowledge transfer cycles." },
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.6 }}
                className="group glass-card p-10 md:p-16 rounded-[3rem] md:rounded-[4.5rem] border border-white/10 hover:bg-white/5 transition-all duration-700 hover:shadow-2xl hover:translate-y-[-10px] glow-border-purple cursor-default"
              >
                <div className="w-20 h-20 md:w-24 md:h-24 bg-slate-900/40 text-5xl md:text-6xl mb-12 flex items-center justify-center rounded-2xl md:rounded-3xl border border-white/5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-sm">
                  {service.icon}
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-8 uppercase tracking-tighter leading-none">{service.title}</h3>
                <p className="text-slate-400 text-base md:text-lg font-medium leading-relaxed lowercase tracking-tight">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-40 px-6 bg-slate-900/40 relative z-10 border-b border-white/5 overflow-hidden">
        <div className="container mx-auto max-w-6xl relative z-10">
          <h2 className="text-[11px] font-black text-slate-400 mb-32 text-center uppercase tracking-[0.5em]">The Intelligence Stack</h2>
          <div className="flex flex-wrap justify-center gap-20">
            {[
              { name: "Python", img: python },
              { name: "TensorFlow", img: ten },
              { name: "PyTorch", img: keras },
              { name: "Scikit-Learn", img: ScikitLearn },
              { name: "Pandas", img: pandas },
              { name: "NumPy", img: numpy },
              { name: "Flask", img: flask },
            ].map((tool, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -12 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="flex flex-col items-center gap-8 group"
              >
                <div className="w-32 h-32 bg-slate-900/40 rounded-[3rem] p-8 border border-white/5 flex items-center justify-center shadow-sm group-hover:shadow-2xl group-hover:shadow-purple-500/10 transition-all duration-500 overflow-hidden">
                  <img src={tool.img} alt={tool.name} className="w-full h-full object-contain transition-all duration-700" />
                </div>
                <span className="text-[11px] font-black text-slate-400 group-hover:text-white uppercase tracking-widest leading-none transition-colors">{tool.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-40 px-6 relative z-10 bg-[#020617] overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-32">
            <h2 className="text-5xl font-black text-white mb-6 tracking-tighter uppercase leading-none">Neural Builds</h2>
            <p className="text-slate-400 font-medium lowercase tracking-tight max-w-sm mx-auto">Intelligent automation and predictive systems delivered to scale.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-12 max-w-7xl mx-auto">
            {[
              {
                title: "Skillora",
                img: skillora,
                tag: "Adaptive Learning",
                desc: "AI-powered educational platform with personalized progress tracking."
              },
              {
                title: "StreetSense",
                img: street,
                tag: "Computer Vision",
                desc: "Smart city monitoring using automated predictive analytics."
              }
            ].map((project, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative bg-slate-900/40 rounded-[3rem] overflow-hidden border border-white/5 hover:shadow-2xl transition-all duration-700 w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)]"
              >
                <div className="relative h-64 overflow-hidden bg-[#020617]">
                  <img src={project.img} alt={project.title} className="w-full h-full object-contain group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent opacity-40 pointer-events-none" />
                  <span className="absolute top-6 left-6 px-4 py-2 bg-white/5 backdrop-blur-md rounded-lg text-[9px] font-black text-white uppercase tracking-widest border border-white/10 shadow-2xl">
                    {project.tag}
                  </span>
                </div>
                <div className="p-10">
                  <h3 className="text-2xl font-black text-white mb-4 tracking-tighter uppercase leading-none">{project.title}</h3>
                  <p className="text-slate-400 text-sm font-medium lowercase tracking-tight">{project.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-48 px-6 relative z-10 bg-[#020617]">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="container mx-auto max-w-5xl relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-24 md:p-32 rounded-[5.5rem] text-center border border-white/10 shadow-[0_60px_120px_rgba(0,0,0,0.5)] relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-80 h-80 bg-purple-500/10 blur-[120px] rounded-full -mr-40 -mt-40" />
            <h2 className="text-5xl md:text-8xl lg:text-[9.5rem] font-black text-white mb-14 tracking-tighter uppercase relative z-10 leading-[0.85] px-4">Artificial <br /><span className="text-gradient from-purple-600 via-indigo-600 to-blue-600">Evolution.</span></h2>
            <div className="flex justify-center gap-10 relative z-10">
              <a
                href="https://wa.me/94723954658"
                className="px-16 py-7 bg-purple-600 text-white font-black rounded-[2.5rem] hover:bg-purple-700 transition-all shadow-[0_20px_40px_rgba(147,51,234,0.3)] hover:scale-105 active:scale-95 uppercase tracking-widest text-xs"
              >
                CONTACT AI ARCHITECTS
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="py-24 px-6 bg-slate-900/40 border-t border-white/5 text-center">
        <p className="text-[10px] font-black text-slate-300 uppercase tracking-[0.5em]">© 2024 LAB Developers — AI Training Excellence.</p>
      </footer>
    </div>
  );
}
